import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import {
  data,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
  type ActionFunctionArgs,
} from "react-router";
import {
  Form,
  useActionData,
  useLoaderData,
  useSearchParams,
} from "react-router";
import { z } from "zod";
import { checkHoneypot } from "~/lib/utils/honeypot.server";
import { EmailSchema } from "~/lib/utils/user-validation";
import {
  Html,
  Container,
  Text,
  Link as EmailLink,
} from "@react-email/components";
import { prepareVerification } from "./verify.server";
import { useIsPending } from "~/lib/utils/misc";
import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { sendEmail } from "~/lib/utils/email.server";
import { requireAnonymous } from "~/lib/utils/auth.server";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { ErrorList, Field } from "~/components/forms";
import { StatusButton } from "~/components/ui/status-button";
import { ProviderConnectionForm, providerNames } from "~/lib/utils/connections";
import { db } from "~/lib/db";
import { users } from "~/lib/db/schema";
import { eq } from "drizzle-orm";
import { Icon } from "~/components/ui/icon";
import { GeneralErrorBoundary } from "~/components/error-boundary";
import { verifySessionStorage } from "~/lib/utils/verification.server";
import { onboardingEmailSessionKey } from "./onboarding";
import { invitationDataSessionKey } from "../team.invites.accept";
import { TenantLink } from "~/components/tenant-link";

export async function loader({ request }: LoaderFunctionArgs) {
  await requireAnonymous(request);

  // Check for invitation data in the session
  const verifySession = await verifySessionStorage.getSession(
    request.headers.get("cookie")
  );

  const invitationEmail = verifySession.get(onboardingEmailSessionKey);
  const pendingInvitation = verifySession.get(invitationDataSessionKey);

  // If there's invitation data, show it in the UI
  return data({
    invitationEmail:
      typeof invitationEmail === "string" ? invitationEmail : null,
    pendingInvitation,
  });
}

const SignupSchema = z.object({
  email: EmailSchema,
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();

  checkHoneypot(formData);

  const submission = await parseWithZod(formData, {
    schema: SignupSchema.superRefine(async (data, ctx) => {
      const existingUsers = await db
        .select({ id: users.id })
        .from(users)
        .where(eq(users.email, data.email))
        .limit(1);

      if (existingUsers.length > 0) {
        ctx.addIssue({
          path: ["email"],
          code: z.ZodIssueCode.custom,
          message: "Un usuario con este email ya existe",
        });
        return;
      }
    }),
    async: true,
  });

  if (submission.status !== "success") {
    return data(
      { result: submission.reply() },
      { status: submission.status === "error" ? 400 : 200 }
    );
  }

  const { email } = submission.value;
  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: "onboarding",
    target: email,
  });

  const response = await sendEmail({
    to: email,
    subject: `¡Bienvenido a ${ENV.BRAND_NAME}!`,
    react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
  });

  if (response.status === "success") {
    return redirect(redirectTo.toString());
  } else {
    return data(
      {
        result: submission.reply({
          formErrors: [response.error.message],
        }),
      },
      {
        status: 500,
      }
    );
  }
}

export default function SignupRoute() {
  const { invitationEmail, pendingInvitation } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const isPending = useIsPending();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");

  const [form, fields] = useForm({
    id: "signup-form",
    constraint: getZodConstraint(SignupSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: SignupSchema });
      return result;
    },
    shouldRevalidate: "onBlur",
    // If we have an invitation email, pre-fill it
    defaultValue: invitationEmail ? { email: invitationEmail } : undefined,
  });

  return (
    <div className="flex flex-col items-center justify-center w-full gap-10 h-dvh">
      <div className="flex flex-col gap-3 text-center">
        <h1 className="font-black text-h1">Registrate a {ENV.BRAND_NAME}</h1>
        {/* Show invitation information if present */}
        {pendingInvitation ? (
          <div className="p-4 mb-4 text-center bg-green-50 border rounded-md border-green-200">
            <p className="text-green-800">
              Has sido invitado a unirte al equipo{" "}
              <strong>{pendingInvitation.teamName}</strong>
            </p>
            <p className="text-sm text-green-600">
              Completa tu registro para aceptar la invitación
            </p>
          </div>
        ) : (
          <p className="text-body-md text-muted-foreground">
            ¿Con qué email te quieres registrar?
          </p>
        )}
      </div>
      <div className="w-full md:max-w-[320px] p-6">
        <Form
          method="POST"
          {...getFormProps(form)}
          className="flex flex-col gap-4"
        >
          <HoneypotInputs />
          <Field
            labelProps={{
              htmlFor: fields.email.id,
              children: "Email",
            }}
            inputProps={{
              ...getInputProps(fields.email, { type: "email" }),
              autoFocus: !invitationEmail, // Only autofocus if no invitation
              autoComplete: "email",
              placeholder: "email@test.com",
              // If there's an invitation email, disable the field to prevent changes
              readOnly: !!invitationEmail,
            }}
            helperText={
              invitationEmail
                ? "Email de la invitación que has recibido"
                : "Te enviaremos un email de confirmación"
            }
            errors={fields.email.errors}
          />
          <ErrorList errors={form.errors} id={form.errorId} />
          <StatusButton
            status={isPending ? "pending" : form.status ?? "idle"}
            type="submit"
            disabled={isPending}
            className="flex items-center group"
          >
            Continuar
            <Icon
              name="arrow-right"
              className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-1 transition-all -mr-2.5"
            />
          </StatusButton>
        </Form>

        {/* Only show alternative signup methods if not dealing with an invitation */}
        {!invitationEmail && (
          <>
            <p className="my-3 text-sm text-center text-muted-foreground">o</p>

            <ul className="flex flex-col gap-5">
              {providerNames.map((providerName) => (
                <li key={providerName}>
                  <ProviderConnectionForm
                    type="signup"
                    providerName={providerName}
                    redirectTo={redirectTo}
                  />
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="flex items-center justify-center gap-2 mt-3 text-body-sm">
          Ya tienes cuenta?
          <TenantLink
            to={
              redirectTo ? `/login?${encodeURIComponent(redirectTo)}` : "/login"
            }
            className="font-semibold underline"
          >
            Inicia sesión aquí
          </TenantLink>
        </div>
      </div>
    </div>
  );
}

export function SignupEmail({
  onboardingUrl,
  otp,
}: {
  onboardingUrl: string;
  otp: string;
}) {
  return (
    <Html lang="es" dir="ltr">
      <Container>
        <h1>
          <Text>¡Bienvenido a {ENV.BRAND_NAME}!</Text>
        </h1>
        <p>
          <Text>
            Aqui está tu código de verificación: <strong>{otp}</strong>
          </Text>
        </p>
        <p>
          <Text>Or clickea aquí para continuar:</Text>
        </p>
        <EmailLink href={onboardingUrl}>{onboardingUrl}</EmailLink>
      </Container>
    </Html>
  );
}

export const meta: MetaFunction = () => {
  return [{ title: "Registrate | Fuel Portfolio" }];
};

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
